import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Table } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import { SUBMIT_QUOTE } from '../graphql/operations';

interface QuoteModalProps {
  open: boolean;
  onClose: () => void;
}

const PRODUCTS = [
  { id: 'visiting-cards',     icon: 'fa-address-card',       label: 'Visiting Cards' },
  { id: 'flex-banners',       icon: 'fa-image',              label: 'Flex Banners' },
  { id: 'flyers',             icon: 'fa-scroll',             label: 'Flyers' },
  { id: 'brochures',          icon: 'fa-book-open',          label: 'Brochures' },
  { id: 'stickers-labels',    icon: 'fa-tags',               label: 'Stickers & Labels' },
  { id: 'wedding-invites',    icon: 'fa-envelope-open-text', label: 'Wedding Invitations' },
  { id: 'posters',            icon: 'fa-photo-film',         label: 'Posters & Artwork' },
  { id: 'id-cards',           icon: 'fa-id-card',            label: 'ID Cards & Badges' },
  { id: 'certificates',       icon: 'fa-award',              label: 'Certificates & Awards' },
  { id: 'other',              icon: 'fa-ellipsis-h',         label: 'Other / Custom' },
];

interface QuoteItem {
  product: string;
  quantity: string;
  size: string;
  description: string;
}

interface FormState {
  name: string;
  phone: string;
  email: string;
}

const defaultForm: FormState = {
  name: '',
  phone: '',
  email: '',
};

const defaultItem: QuoteItem = {
  product: '',
  quantity: '',
  size: '',
  description: '',
};

const QuoteModal: React.FC<QuoteModalProps> = ({ open, onClose }) => {
  const [submitQuoteMutation, { loading: submittingQuote }] = useMutation(SUBMIT_QUOTE);
  
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(defaultForm);
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [currentItem, setCurrentItem] = useState<QuoteItem>(defaultItem);
  
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [itemErrors, setItemErrors] = useState<Partial<QuoteItem>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [refId, setRefId] = useState('');

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(1);
        setForm(defaultForm);
        setItems([]);
        setCurrentItem(defaultItem);
        setErrors({});
        setItemErrors({});
        setSubmitError('');
        setSubmitted(false);
        setRefId('');
      }, 300);
    }
  }, [open]);

  // Keyboard ESC
  const handleKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, handleKey]);

  const updateForm = (field: keyof FormState, value: string) => {
    setForm(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: '' }));
  };

  const updateItem = (field: keyof QuoteItem, value: string) => {
    setCurrentItem(p => ({ ...p, [field]: value }));
    if (itemErrors[field]) setItemErrors(p => ({ ...p, [field]: '' }));
  };

  const validateItem = (): boolean => {
    const e: Partial<QuoteItem> = {};
    if (!currentItem.product) e.product = 'Please select a product.';
    if (!currentItem.quantity.trim()) e.quantity = 'Quantity is required.';
    setItemErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAddItem = () => {
    if (validateItem()) {
      setItems([...items, currentItem]);
      setCurrentItem(defaultItem);
    }
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const validateStep = (s: number): boolean => {
    if (s === 1) {
      if (items.length === 0 && currentItem.product) {
        // user selected a product but forgot to click Add Item
        const e: Partial<QuoteItem> = {};
        e.product = 'Please click "Add Line Item" before proceeding.';
        setItemErrors(e);
        return false;
      }
      if (items.length === 0) {
        const e: Partial<QuoteItem> = {};
        e.product = 'Please add at least one item.';
        setItemErrors(e);
        return false;
      }
      return true;
    }
    
    if (s === 2) {
      const e: Partial<FormState> = {};
      if (!form.name.trim()) e.name = 'Name is required.';
      if (!form.phone.trim()) e.phone = 'Phone is required.';
      else if (!/^\d{10}$/.test(form.phone.replace(/[\s\-+]/g, '').slice(-10)))
        e.phone = 'Enter a valid 10-digit number.';
      if (form.email && !/\S+@\S+\.\S+/.test(form.email))
        e.email = 'Enter a valid email address.';
      setErrors(e);
      return Object.keys(e).length === 0;
    }
    return true;
  };

  const next = () => {
    if (validateStep(step)) {
      setStep(p => p + 1);
    }
  };

  const prev = () => setStep(p => p - 1);

  const submit = async () => {
    if (!validateStep(2)) return;
    setSubmitError('');
    try {
      // Map items to the format expected by the backend
      const mappedItems = items.map(item => ({
        product: item.product,
        quantity: item.quantity,
        size: item.size || '',
        description: item.description || ''
      }));

      const { data } = await submitQuoteMutation({
        variables: {
          input: {
            items: mappedItems,
            name: form.name,
            phone: form.phone,
            email: form.email || '',
          },
        },
      });
      if (data?.submitQuote?.success) {
        setRefId(data.submitQuote.quoteId);
        setSubmitted(true);
      } else {
        setSubmitError(data?.submitQuote?.message || 'Failed to submit quote request. Please try again.');
      }
    } catch (err: any) {
      setSubmitError(err.message || 'Error connecting to server. Please try again.');
    }
  };

  const getWhatsAppLink = () => {
    let msg = `Hi Prathitha Digital Printers! 👋\nI'd like to get a *Quote* for:\n\n*Reference:* ${refId}\n\n`;
    
    items.forEach((item, idx) => {
      const prod = PRODUCTS.find(p => p.id === item.product)?.label || item.product;
      msg += `*Item ${idx + 1}:* ${prod}\n`;
      msg += `*Quantity:* ${item.quantity}\n`;
      msg += `*Size / Dimensions:* ${item.size || 'Not specified'}\n`;
      if (item.description) msg += `*Requirements:* ${item.description}\n`;
      msg += `\n`;
    });
    
    msg += `*Contact Info:*\n*Name:* ${form.name}\n*Phone:* ${form.phone}\n${form.email ? `*Email:* ${form.email}\n` : ''}`;
    msg += `\nPlease share the pricing and availability. Thank you!`;
    return `https://wa.me/919150808130?text=${encodeURIComponent(msg)}`;
  };

  if (!open) return null;

  return (
    <div
      className={`qm-backdrop ${open ? 'qm-backdrop-open' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      aria-modal="true"
      role="dialog"
    >
      <div className={`qm-panel ${open ? 'qm-panel-open' : ''}`} style={{ maxWidth: '800px' }}>

        {/* ── HEADER ── */}
        <div className="qm-header">
          <div className="qm-header-left">
            <div className="qm-header-icon">
              <i className="fas fa-file-invoice"></i>
            </div>
            <div>
              <div className="qm-header-title">Get a Quote</div>
              <div className="qm-header-sub">Free, no-obligation estimate</div>
            </div>
          </div>
          <button className="qm-close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* ── STEP INDICATOR ── */}
        {!submitted && (
          <div className="qm-steps">
            {['Configure Items', 'Contact Details'].map((label, i) => {
              const num = i + 1;
              const active = step === num;
              const done = step > num;
              return (
                <React.Fragment key={label}>
                  <div className={`qm-step ${active ? 'qm-step-active' : done ? 'qm-step-done' : ''}`}>
                    <div className="qm-step-circle">
                      {done ? <i className="fas fa-check"></i> : num}
                    </div>
                    <span className="qm-step-label">{label}</span>
                  </div>
                  {i < 1 && <div className={`qm-step-line ${done ? 'qm-step-line-done' : ''}`}></div>}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* ── BODY ── */}
        <div className="qm-body">

          {/* SUCCESS */}
          {submitted ? (
            <div className="qm-success">
              <div className="qm-success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3>Quote Request Sent!</h3>
              <p>Your reference number is</p>
              <div className="qm-ref-id">{refId}</div>
              <p className="qm-success-sub">
                Click below to send your requirements directly to our WhatsApp team. We'll respond within minutes!
              </p>
              <a
                href={getWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                className="qm-btn-whatsapp"
              >
                <i className="fab fa-whatsapp"></i> Send via WhatsApp
              </a>
              <button
                className="qm-btn-outline mt-3"
                onClick={() => { setStep(1); setForm(defaultForm); setItems([]); setSubmitted(false); setErrors({}); }}
              >
                New Quote Request
              </button>
            </div>
          ) : (
            <>
              {submitError && <div className="qm-error-banner mb-3"><i className="fas fa-exclamation-circle me-2"></i>{submitError}</div>}
              
              {/* STEP 1 – Add Items */}
              {step === 1 && (
                <div className="qm-step-content">
                  
                  {/* Added Items Table */}
                  {items.length > 0 && (
                    <div className="mb-4">
                      <h5 className="qm-section-title">Added Line Items</h5>
                      <div className="table-responsive">
                        <Table size="sm" bordered hover className="align-middle" style={{ fontSize: '14px' }}>
                          <thead className="table-light">
                            <tr>
                              <th>Product</th>
                              <th>Quantity</th>
                              <th>Size</th>
                              <th>Requirements</th>
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, idx) => (
                              <tr key={idx}>
                                <td className="fw-bold">{PRODUCTS.find(p => p.id === item.product)?.label || item.product}</td>
                                <td>{item.quantity}</td>
                                <td>{item.size || '-'}</td>
                                <td style={{ maxWidth: '200px' }} className="text-truncate">{item.description || '-'}</td>
                                <td className="text-center">
                                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveItem(idx)}>
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}

                  <h5 className="qm-section-title">Configure New Line Item</h5>
                  {itemErrors.product && <div className="qm-error-banner"><i className="fas fa-exclamation-circle"></i> {itemErrors.product}</div>}
                  
                  <div className="qm-product-grid mb-3">
                    {PRODUCTS.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        className={`qm-product-tile ${currentItem.product === p.id ? 'qm-product-tile-active' : ''}`}
                        onClick={() => updateItem('product', p.id)}
                        style={{ padding: '10px' }}
                      >
                        <div className="qm-product-tile-icon" style={{ fontSize: '20px', marginBottom: '8px' }}>
                          <i className={`fas ${p.icon}`}></i>
                        </div>
                        <span style={{ fontSize: '13px' }}>{p.label}</span>
                      </button>
                    ))}
                  </div>

                  {currentItem.product && (
                    <div className="bg-light p-3 rounded mb-3 border">
                      <Row className="g-3">
                        <Col sm={6}>
                          <div className="qm-field-group mb-0">
                            <label className="qm-label">Quantity <span className="qm-required">*</span></label>
                            <input
                              className={`qm-input ${itemErrors.quantity ? 'qm-input-error' : ''}`}
                              placeholder="e.g. 100, 500, 1000"
                              value={currentItem.quantity}
                              onChange={e => updateItem('quantity', e.target.value)}
                            />
                            {itemErrors.quantity && <div className="qm-field-error">{itemErrors.quantity}</div>}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="qm-field-group mb-0">
                            <label className="qm-label">Size / Dimensions <span className="qm-optional">(optional)</span></label>
                            <input
                              className="qm-input"
                              placeholder="e.g. A4, 3.5×2 inch, 4×6 ft"
                              value={currentItem.size}
                              onChange={e => updateItem('size', e.target.value)}
                            />
                          </div>
                        </Col>
                        <Col sm={12}>
                          <div className="qm-field-group mb-0">
                            <label className="qm-label">Additional Requirements <span className="qm-optional">(optional)</span></label>
                            <textarea
                              className="qm-input qm-textarea"
                              placeholder="Describe your design, finish, material preferences or any specific needs..."
                              value={currentItem.description}
                              onChange={e => updateItem('description', e.target.value)}
                              rows={2}
                            />
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}

                  <div className="d-flex justify-content-end">
                    <button type="button" className="qm-btn-outline" onClick={handleAddItem}>
                      <i className="fas fa-plus me-1"></i> Add Line Item
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2 – Contact */}
              {step === 2 && (
                <div className="qm-step-content">
                  <h5 className="qm-section-title">Your Contact Details</h5>
                  <p className="qm-section-sub">We'll use these to send your personalised quote.</p>
                  <div className="qm-field-group">
                    <label className="qm-label">Full Name <span className="qm-required">*</span></label>
                    <input
                      className={`qm-input ${errors.name ? 'qm-input-error' : ''}`}
                      placeholder="Your name"
                      value={form.name}
                      onChange={e => updateForm('name', e.target.value)}
                    />
                    {errors.name && <div className="qm-field-error">{errors.name}</div>}
                  </div>
                  <Row className="g-3">
                    <Col sm={6}>
                      <div className="qm-field-group">
                        <label className="qm-label">Phone <span className="qm-required">*</span></label>
                        <input
                          className={`qm-input ${errors.phone ? 'qm-input-error' : ''}`}
                          placeholder="10-digit mobile number"
                          value={form.phone}
                          onChange={e => updateForm('phone', e.target.value)}
                          inputMode="numeric"
                          maxLength={15}
                        />
                        {errors.phone && <div className="qm-field-error">{errors.phone}</div>}
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="qm-field-group">
                        <label className="qm-label">Email <span className="qm-optional">(optional)</span></label>
                        <input
                          className={`qm-input ${errors.email ? 'qm-input-error' : ''}`}
                          placeholder="your@email.com"
                          value={form.email}
                          onChange={e => updateForm('email', e.target.value)}
                          type="email"
                        />
                        {errors.email && <div className="qm-field-error">{errors.email}</div>}
                      </div>
                    </Col>
                  </Row>

                  {/* Summary preview */}
                  <div className="qm-summary-box">
                    <div className="qm-summary-title"><i className="fas fa-clipboard-list me-2"></i>Quote Summary ({items.length} items)</div>
                    {items.map((item, idx) => (
                      <div key={idx} className="qm-summary-row mb-1">
                        <span>{idx + 1}. {PRODUCTS.find(p => p.id === item.product)?.label || item.product}</span>
                        <span>Qty: {item.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── FOOTER NAV ── */}
        {!submitted && (
          <div className="qm-footer">
            {step > 1 ? (
              <button className="qm-btn-back" onClick={prev}>
                <i className="fas fa-arrow-left me-1"></i> Back
              </button>
            ) : (
              <button className="qm-btn-back" onClick={onClose}>Cancel</button>
            )}
            <div className="qm-step-dots">
              {[1, 2].map(n => (
                <div key={n} className={`qm-dot ${step === n ? 'qm-dot-active' : step > n ? 'qm-dot-done' : ''}`}></div>
              ))}
            </div>
            {step < 2 ? (
              <button className="qm-btn-next" onClick={next}>
                Next <i className="fas fa-arrow-right ms-1"></i>
              </button>
            ) : (
              <button className="qm-btn-submit" onClick={submit} disabled={submittingQuote}>
                {submittingQuote ? (
                  <><i className="fas fa-spinner fa-spin me-1"></i> Submitting...</>
                ) : (
                  <><i className="fas fa-paper-plane me-1"></i> Get My Quote</>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteModal;
